import React, { useEffect, useId, useRef, useState } from 'react';
import { adminBrightPatternTemplateApi } from '../../../api/adminBrightPatternTemplate.api';
import { apiHandler, unwrapData } from '../../../utils/apiHandler';
import './../../bright-pattern-templates/bright-pattern-templates.css';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidId(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

export default function BrightPatternTemplateSelect({
  value,
  onChange,
  placeholder = 'Search dashboard template…',
  emptyLabel = 'No active templates found',
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectedId = isValidId(value) ? value.trim() : '';
  const selected = options.find((item) => String(item.id) === selectedId) || null;
  const displayValue = open ? query : (selected?.name || '');

  const filtered = options.filter((item) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return item.name.toLowerCase().includes(q) || item.slug.toLowerCase().includes(q);
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await apiHandler(() => adminBrightPatternTemplateApi.list({ includeInactive: false }));
      if (!cancelled && result.success) {
        setOptions(unwrapData(result) || []);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedId || options.some((item) => item.id === selectedId)) return;
    let cancelled = false;
    (async () => {
      const result = await apiHandler(() => adminBrightPatternTemplateApi.getById(selectedId));
      if (!cancelled && result.success) {
        const row = unwrapData(result);
        if (row) setOptions((prev) => [...prev, row]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, options]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (item) => {
    onChange(item?.id || null);
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  const onKeyDown = (event) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      pick(filtered[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className="bpt-select" ref={rootRef}>
      <input
        id={listId}
        type="text"
        className="bpt-select-input"
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
      />
      {open && (
        <div className="bpt-select-list" role="listbox">
          {loading && <div className="bpt-select-option">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="bpt-select-option">{emptyLabel}</div>
          )}
          {!loading && filtered.map((item, index) => (
            <div
              key={item.id}
              role="option"
              className={`bpt-select-option${index === activeIndex ? ' is-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
            >
              {item.name}
              <small>{item.slug}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
